// Value formats for configured table rows (e.g. MonthlyHistoricalPivotTable
// rows: { "format": "date" | "percent" | "km" | "number" | "age" | "text" }).
// Pure, screen-side formatting; empty / unusable values render as an em-dash.

import { formatComputedDate } from "../DatePicker/DateConverter";
import { formatCompactDistanceKm } from "./NumberFormatter";

export const EMPTY_VALUE = "—";

// Average month length used to turn a day count into months.
const DAYS_PER_MONTH = 30.4375;

const isBlank = (value) =>
    value === null || value === undefined || (typeof value === "string" && value.trim() === "");

const toNumber = (value) => {
    if (isBlank(value)) return null;
    const n = parseFloat(value);
    return isNaN(n) ? null : n;
};

/**
 * Days -> "1 yr 4 mo" / "7 mo" / "0 mo" / "2 yr" (whole years show no "0 mo").
 * months = floor(days / 30.4375). Negative or non-numeric -> em-dash.
 */
export const formatAgeFromDays = (days) => {
    const n = toNumber(days);
    if (n === null || n < 0) return EMPTY_VALUE;
    const months = Math.floor(n / DAYS_PER_MONTH);
    const years = Math.floor(months / 12);
    const rest = months % 12;
    if (years === 0) return `${rest} mo`;
    return rest === 0 ? `${years} yr` : `${years} yr ${rest} mo`;
};

/**
 * Round half up (away from zero) to `decimals` places. Number.prototype.toFixed
 * rounds the binary value, so 86.45 can become 86.4; scaling through an
 * exponent string avoids that.
 */
export const roundHalfUp = (n, decimals = 0) => {
    const sign = n < 0 ? -1 : 1;
    const abs = Math.abs(n);
    // Exponent-notation inputs (very large / tiny) cannot take the string trick.
    if (String(abs).includes("e")) return sign * (Math.round(abs * 10 ** decimals) / 10 ** decimals);
    const shifted = Math.round(Number(`${abs}e${decimals}`));
    return sign * Number(`${shifted}e-${decimals}`);
};

/** Whole number (half up, like the CSV) with thousands separators ("12,345"); non-numeric -> em-dash. */
export const formatWholeNumber = (value) => {
    const n = toNumber(value);
    return n === null ? EMPTY_VALUE : roundHalfUp(n, 0).toLocaleString("en-US");
};


/** One decimal (half up) + "%" ("86.4%"); non-numeric -> em-dash. */
export const formatPercentOneDecimal = (value) => {
    const n = toNumber(value);
    return n === null ? EMPTY_VALUE : `${roundHalfUp(n, 1).toFixed(1)}%`;
};

/**
 * Format a raw value by a configured format name. Unknown formats fall back to
 * "text" (shown as is).
 */
export const formatPivotValue = (value, format) => {
    if (isBlank(value)) return EMPTY_VALUE;
    switch (format) {
        case "date":
            return formatComputedDate(value);
        case "percent":
            return formatPercentOneDecimal(value);
        case "km":
            return formatCompactDistanceKm(value, 1) || EMPTY_VALUE;
        case "number":
            return formatWholeNumber(value);
        case "age":
            return formatAgeFromDays(value);
        default:
            return String(value);
    }
};

/**
 * CSV value by format: like the screen but machine-friendly where numbers are
 * concerned. date "31 Aug 2026", percent "86.4%" (half up), km whole number
 * (no unit, no separators), number whole number (no separators), age
 * "1 yr 4 mo", text as is. Empty / unusable -> "".
 */
export const formatPivotCsvValue = (value, format) => {
    if (isBlank(value)) return "";
    let text;
    switch (format) {
        case "km":
        case "number": {
            const n = toNumber(value);
            text = n === null ? EMPTY_VALUE : String(roundHalfUp(n, 0));
            break;
        }
        default:
            text = formatPivotValue(value, format);
    }
    return text === EMPTY_VALUE ? "" : text;
};
